'use client';

import { useState, useEffect, useCallback, useMemo } from 'react';
import { reduxLogger } from '@/core/utils/logger';
import { Check, Bell, BellOff, MonitorSmartphone, Mail, MessageSquare, UserCheck, AlertTriangle, Save, Shield, CreditCard, Loader2, Phone, Smartphone, Lock } from 'lucide-react';
import { showToast } from '@/shared/utils/toast';
import { useAppSelector } from '@/core/store/hooks';
import { isAdmin as checkIsAdmin } from '@/core/auth/utils/roleChecks';

type NotificationChannel = 'inApp' | 'push' | 'email' | 'sms';

import type { NotificationPreferences } from '@/api/features/users/types';

import {
  useGetNotificationSettingsQuery,
  useUpdateNotificationSettingsMutation,
  useGetVapidPublicKeyQuery,
  useSubscribeToPushMutation,
  useUnsubscribeFromPushMutation,
  useGetPushSubscriptionsQuery,
  useTestPushNotificationMutation,
  useGetUserProfileQuery
} from '@/api/features/users';
import { usePushNotifications } from '@/features/notifications';

// Channel icon component
const ChannelIcon = ({ channel }: { channel: NotificationChannel }) => {
  switch (channel) {
    case 'inApp':
      return <Bell size={16} />;
    case 'push':
      return <MonitorSmartphone size={16} />;
    case 'email':
      return <Mail size={16} />;
    case 'sms':
      return <Phone size={16} />;
    default:
      return null;
  }
};

/**
 * NotificationsContainer - Orchestrates notification settings
 *
 * Pattern: Container owns RTK Query hooks for notification settings,
 * push subscription management, and renders the settings UI.
 */
export default function NotificationsContainer() {
  const [isSaved, setIsSaved] = useState(false);

  // Check if user is admin
  const userProfile = useAppSelector(state => state.userProfile.session);
  const isAdmin = useMemo(() => checkIsAdmin(userProfile), [userProfile]);

  // RTK QUERY: Single source of truth for notification settings
  const { data: settingsData, isLoading } = useGetNotificationSettingsQuery();
  const [updateSettingsMutation] = useUpdateNotificationSettingsMutation();

  // RTK QUERY: User profile for phone number validation (SMS gating)
  const { data: userProfileData } = useGetUserProfileQuery();

  // Push notification state and hooks
  const pushNotifications = usePushNotifications();
  const { data: vapidData } = useGetVapidPublicKeyQuery();
  const { data: pushSubscriptions, refetch: refetchSubscriptions } = useGetPushSubscriptionsQuery();
  const [subscribeToPush] = useSubscribeToPushMutation();
  const [unsubscribeFromPush] = useUnsubscribeFromPushMutation();
  const [testPush] = useTestPushNotificationMutation();
  const [isPushLoading, setIsPushLoading] = useState(false);

  // LOCAL STATE: Form state only - match component expectations
  // Reordered to prioritize workflow-related notifications
  const [localPreferences, setLocalPreferences] = useState<NotificationPreferences>({
    userId: '',
    masterEnabled: true,
    categories: {
      assigned_client_messages: { inApp: true, push: false, email: false },
      all_client_messages: { inApp: true, push: false, email: false },
      team_chat: { inApp: true, push: false, email: false },
      assignments: { inApp: true, push: false, email: false },
      security_alerts: { inApp: true, push: false, email: true, sms: false },
      system_errors: { inApp: true, push: false, email: true, sms: false },
      billing: { inApp: true, push: false, email: true, sms: false },
      marketing: { inApp: false, push: false, email: false, sms: false }
    }
  });

  // Update local state when data loads
  useEffect(() => {
    if (settingsData && typeof settingsData === 'object') {
      const data = settingsData as unknown as Record<string, unknown>;

      // Default categories structure
      const defaultCategories: NotificationPreferences['categories'] = {
        assigned_client_messages: { inApp: true, push: false, email: false },
        all_client_messages: { inApp: true, push: false, email: false },
        team_chat: { inApp: true, push: false, email: false },
        assignments: { inApp: true, push: false, email: false },
        security_alerts: { inApp: true, push: false, email: true, sms: false },
        system_errors: { inApp: true, push: false, email: true, sms: false },
        billing: { inApp: true, push: false, email: true, sms: false },
        marketing: { inApp: false, push: false, email: false, sms: false }
      };

      const ensuredPrefs: NotificationPreferences = {
        userId: (data.userId as string) || '',
        masterEnabled: typeof data.masterEnabled === 'boolean' ? data.masterEnabled : true,
        categories: (typeof data.categories === 'object' && data.categories !== null ? data.categories : defaultCategories) as NotificationPreferences['categories']
      };
      setLocalPreferences(ensuredPrefs); // eslint-disable-line react-hooks/set-state-in-effect -- syncs local state from server preferences
    }
  }, [settingsData]);

  // Check if any SMS channel is enabled across all categories
  const hasAnySmsEnabled = (prefs: NotificationPreferences): boolean => {
    return Object.values(prefs.categories).some(
      (settings) => settings.sms === true
    );
  };

  // Strip SMS from all categories (returns new preferences with sms disabled)
  const stripSmsFromPreferences = (prefs: NotificationPreferences): NotificationPreferences => ({
    ...prefs,
    categories: Object.fromEntries(
      Object.entries(prefs.categories).map(([key, settings]) => [
        key,
        { ...settings, sms: false }
      ])
    ) as NotificationPreferences['categories']
  });

  // Handle saving settings
  const handleSaveSettings = async () => {
    if (!localPreferences) return;

    let preferencesToSave = localPreferences;

    // Gate SMS: if any SMS toggle is enabled, verify the user has a phone number
    if (hasAnySmsEnabled(localPreferences)) {
      const userPhone = userProfileData?.phoneNumber;
      if (!userPhone || userPhone.trim() === '') {
        showToast.error(
          'SMS notifications require a phone number. Please add one in Settings > Personal Information, then try again.'
        );
        // Strip SMS toggles and update local state so the UI reflects reality
        preferencesToSave = stripSmsFromPreferences(localPreferences);
        setLocalPreferences(preferencesToSave);
      }
    }

    try {
      await updateSettingsMutation({
        masterEnabled: preferencesToSave.masterEnabled,
        categories: preferencesToSave.categories
      }).unwrap();
      setIsSaved(true);
      setTimeout(() => setIsSaved(false), 3000);
      showToast.success('Notification settings saved');
    } catch (error) {
      reduxLogger.error('Failed to save notification settings:', error);
      showToast.error('Failed to save notification settings');
    }
  };

  // Format the channel name for display
  const formatChannelName = (channel: NotificationChannel): string => {
    switch (channel) {
      case 'inApp':
        return 'In-App';
      case 'push':
        return 'Push';
      case 'email':
        return 'Email';
      case 'sms':
        return 'SMS';
      default:
        return channel;
    }
  };

  // Get icon and details for notification category
  const getCategoryInfo = (categoryKey: string) => {
    switch (categoryKey) {
      case 'all_client_messages':
        return {
          icon: <MessageSquare className="text-blue-500" size={18} />,
          title: 'All Client Messages',
          description: 'Messages from any client conversations, whether assigned to you or not'
        };
      case 'assigned_client_messages':
        return {
          icon: <MessageSquare className="text-blue-600" size={18} />,
          title: 'Assigned Client Messages',
          description: 'Messages from client conversations where you are specifically assigned'
        };
      case 'team_chat':
        return {
          icon: <MessageSquare className="text-green-500" size={18} />,
          title: 'Team Chat Messages',
          description: 'Messages from team members and colleagues'
        };
      case 'assignments':
        return {
          icon: <UserCheck className="text-orange-500" size={18} />,
          title: 'Assignments',
          description: 'When you are assigned to conversations or tasks'
        };
      case 'system_errors':
        return {
          icon: <AlertTriangle className="text-red-500" size={18} />,
          title: 'App Error/System Issue',
          description: 'Critical system errors and application issues',
          isAdminOnly: true
        };
      case 'security_alerts':
        return {
          icon: <Shield className="text-purple-500" size={18} />,
          title: 'Account Security Alert',
          description: 'Important security updates and login alerts',
          isAdminOnly: true
        };
      case 'marketing':
        return {
          icon: <Bell className="text-indigo-500" size={18} />,
          title: 'Marketing/Product Updates',
          description: 'Product announcements and marketing communications',
          isAdminOnly: true
        };
      case 'billing':
        return {
          icon: <CreditCard className="text-yellow-500" size={18} />,
          title: 'Billing/Payment Issues',
          description: 'Payment notifications and billing alerts',
          isAdminOnly: true
        };
      default:
        return {
          icon: <Bell size={18} />,
          title: categoryKey,
          description: '',
          isAdminOnly: false
        };
    }
  };

  // Get available channels for each category
  const getAvailableChannels = (categoryKey: string): NotificationChannel[] => {
    switch (categoryKey) {
      case 'all_client_messages':
      case 'assigned_client_messages':
      case 'team_chat':
      case 'assignments':
        return ['inApp', 'push'];
      case 'marketing':
        return ['email'];
      case 'system_errors':
      case 'security_alerts':
      case 'billing':
        return ['inApp', 'push', 'email', 'sms'];
      default:
        return ['inApp', 'push'];
    }
  };

  // Filter categories based on admin status
  const getVisibleCategories = () => {
    const allCategories = Object.keys(localPreferences.categories);
    // Categories hidden from the UI (not user-configurable)
    const hiddenCategories = ['marketing', 'billing'];

    // Desired explicit order
    const desiredOrder = [
      'all_client_messages',
      'assigned_client_messages',
      'team_chat',
      'assignments',
      'system_errors',
      'security_alerts',
    ];

    // Filter out hidden categories
    const visible = allCategories.filter((category) => !hiddenCategories.includes(category));

    const orderIndex = (key: string) => {
      const idx = desiredOrder.indexOf(key);
      return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
    };

    return [...visible].sort((a, b) => {
      const aIdx = orderIndex(a);
      const bIdx = orderIndex(b);
      if (aIdx !== bIdx) return aIdx - bIdx;
      return a.localeCompare(b);
    });
  };

  // Handle push notification subscription
  const handlePushSubscription = useCallback(async (enable: boolean): Promise<boolean> => {
    setIsPushLoading(true);

    try {
      if (enable && vapidData?.publicKey) {
        const subscription = await pushNotifications.subscribe(vapidData.publicKey);
        if (subscription) {
          const json = subscription.toJSON();
          const userAgent = navigator.userAgent;
          let browser = 'Unknown';
          let operatingSystem = 'Unknown';

          if (userAgent.includes('Firefox')) browser = 'Firefox';
          else if (userAgent.includes('Edg')) browser = 'Edge';
          else if (userAgent.includes('Chrome')) browser = 'Chrome';
          else if (userAgent.includes('Safari')) browser = 'Safari';

          if (userAgent.includes('Windows')) operatingSystem = 'Windows';
          else if (userAgent.includes('Mac')) operatingSystem = 'macOS';
          else if (userAgent.includes('Linux')) operatingSystem = 'Linux';
          else if (userAgent.includes('Android')) operatingSystem = 'Android';
          else if (userAgent.includes('iPhone') || userAgent.includes('iPad')) operatingSystem = 'iOS';

          const subscriptionData = {
            endpoint: json.endpoint ?? '',
            keys: {
              p256dh: json.keys?.p256dh || '',
              auth: json.keys?.auth || ''
            },
            deviceInfo: {
              deviceName: `${browser} on ${operatingSystem}`,
              browser,
              operatingSystem
            }
          };

          await subscribeToPush(subscriptionData).unwrap();
          showToast.success('Push notifications enabled successfully');
          await refetchSubscriptions();
          return true;
        }
        return false;
      } else if (!enable && pushNotifications.subscription) {
        const endpoint = pushNotifications.subscription.endpoint;
        await pushNotifications.unsubscribe();
        await unsubscribeFromPush({ endpoint }).unwrap();
        showToast.success('Push notifications disabled');
        await refetchSubscriptions();
        return true;
      }
      return false;
    } catch (error) {
      reduxLogger.error('Error managing push subscription:', error);
      showToast.error('Failed to update push notification settings');
      return false;
    } finally {
      setIsPushLoading(false);
    }
  }, [vapidData, pushNotifications, subscribeToPush, unsubscribeFromPush, refetchSubscriptions]);

  // Test push notification
  const handleTestPush = useCallback(async () => {
    try {
      await testPush().unwrap();
      showToast.success('Test notification sent! Check your device.');
    } catch (error) {
      reduxLogger.error('Error sending test push:', error);
      showToast.error('Failed to send test notification');
    }
  }, [testPush]);

  // Handle category toggle
  const handleCategoryToggle = async (categoryKey: string, channel: NotificationChannel) => {
    // If enabling push for any category and not subscribed, subscribe first
    if (channel === 'push' && !localPreferences.categories[categoryKey as keyof typeof localPreferences.categories].push) {
      if (!pushNotifications.isSupported) {
        showToast.error('Push notifications are not supported in your browser');
        return;
      }

      if (!vapidData?.publicKey) {
        showToast.error('Push notification service is not configured. Please try again later.');
        return;
      }

      if (!pushNotifications.isSubscribed) {
        const success = await handlePushSubscription(true);
        if (!success) {
          return;
        }
      }
    }

    setLocalPreferences(prev => ({
      ...prev,
      categories: {
        ...prev.categories,
        [categoryKey]: {
          ...prev.categories[categoryKey as keyof typeof prev.categories],
          [channel]: !prev.categories[categoryKey as keyof typeof prev.categories][channel]
        }
      }
    }));
  };

  if (isLoading || !localPreferences) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-text-secondary">Loading notification settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto" data-tour-id="notifications-page">
      <div className="p-6 w-full max-w-4xl mx-auto pb-24">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold mb-2 text-text-primary">Notifications</h1>
          <p className="text-text-secondary">Configure how and when you receive notifications</p>
        </div>

        <MasterSwitchSection
          localPreferences={localPreferences}
          setLocalPreferences={setLocalPreferences}
        />

        <NotificationCategoriesSection
          localPreferences={localPreferences}
          isAdmin={isAdmin}
          isPushLoading={isPushLoading}
          pushNotifications={pushNotifications}
          getVisibleCategories={getVisibleCategories}
          getCategoryInfo={getCategoryInfo}
          getAvailableChannels={getAvailableChannels}
          formatChannelName={formatChannelName}
          handleCategoryToggle={handleCategoryToggle}
        />

        <PushDevicesSection
          pushNotifications={pushNotifications}
          isPushLoading={isPushLoading}
          handleTestPush={handleTestPush}
          handlePushSubscription={handlePushSubscription}
          pushSubscriptions={pushSubscriptions}
          unsubscribeFromPush={unsubscribeFromPush}
          refetchSubscriptions={refetchSubscriptions}
        />

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={handleSaveSettings}
            disabled={!localPreferences}
            className="flex items-center px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSaved ? (
              <>
                <Check size={16} className="mr-2" />
                Saved
              </>
            ) : (
              <>
                <Save size={16} className="mr-2" />
                Save Settings
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Sub-component: Master Switch ---
function MasterSwitchSection({
  localPreferences,
  setLocalPreferences,
}: {
  localPreferences: NotificationPreferences;
  setLocalPreferences: React.Dispatch<React.SetStateAction<NotificationPreferences>>;
}) {
  return (
    <div className="bg-card-bg rounded-lg shadow-sm border border-border overflow-hidden mb-8">
      <div className="px-6 py-5 border-b border-border flex justify-between items-center bg-card-header">
        <h2 className="text-lg font-semibold text-text-primary">Notification Settings</h2>
        <button
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary/50 ${localPreferences.masterEnabled ? 'bg-primary' : 'bg-gray-300'}`}
          onClick={() => setLocalPreferences({ ...localPreferences, masterEnabled: !localPreferences.masterEnabled })}
          role="switch" aria-checked={localPreferences.masterEnabled}>
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${localPreferences.masterEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
      </div>
      <div className="px-6 py-6">
        <div className="flex items-center">
          {localPreferences.masterEnabled ? <Bell size={20} className="text-primary mr-3" /> : <BellOff size={20} className="text-text-disabled mr-3" />}
          <div>
            <h3 className="font-medium text-text-primary">
              {localPreferences.masterEnabled ? 'Notifications are enabled' : 'Notifications are disabled'}
            </h3>
            <p className="text-sm text-text-secondary mt-1">
              {localPreferences.masterEnabled
                ? 'You will receive notifications according to your preferences below'
                : 'You will not receive any notifications until you enable this setting'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Sub-component: Notification Categories ---
function NotificationCategoriesSection({
  localPreferences, isAdmin, isPushLoading, pushNotifications,
  getVisibleCategories, getCategoryInfo, getAvailableChannels,
  formatChannelName, handleCategoryToggle,
}: {
  localPreferences: NotificationPreferences;
  isAdmin: boolean;
  isPushLoading: boolean;
  pushNotifications: ReturnType<typeof usePushNotifications>;
  getVisibleCategories: () => string[];
  getCategoryInfo: (key: string) => { icon: React.ReactNode; title: string; description: string; isAdminOnly?: boolean };
  getAvailableChannels: (key: string) => NotificationChannel[];
  formatChannelName: (ch: NotificationChannel) => string;
  handleCategoryToggle: (categoryKey: string, channel: NotificationChannel) => Promise<void>;
}) {
  return (
    <div className="bg-card-bg rounded-lg shadow-sm border border-border overflow-hidden mb-8">
      <div className="px-6 py-5 border-b border-border bg-card-header">
        <h2 className="text-lg font-semibold text-text-primary">Notification Categories</h2>
        <p className="text-sm text-text-secondary mt-1">Choose which types of notifications you want to receive and how</p>
      </div>
      <div className="px-6 py-6">
        <div className="space-y-6">
          {getVisibleCategories().map((categoryKey) => {
            const categoryInfo = getCategoryInfo(categoryKey);
            const categorySettings = localPreferences.categories[categoryKey as keyof typeof localPreferences.categories];
            const availableChannels = getAvailableChannels(categoryKey);
            const isAdminOnlyCategory = categoryInfo.isAdminOnly && !isAdmin;
            return (
              <div key={categoryKey} className={`border-b border-border pb-6 last:border-b-0 last:pb-0 ${isAdminOnlyCategory ? 'opacity-60' : ''}`}>
                <div className="flex items-center mb-4">
                  <div className={`w-10 h-10 ${isAdminOnlyCategory ? 'bg-gray-100' : 'bg-primary/10'} rounded-lg flex items-center justify-center mr-4`}>
                    {categoryInfo.icon}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className={`text-base font-medium ${isAdminOnlyCategory ? 'text-text-secondary' : 'text-text-primary'}`}>{categoryInfo.title}</h3>
                      {categoryInfo.isAdminOnly && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 rounded-full">
                          <Lock size={10} />Admin Only
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-text-secondary mt-1">
                      {categoryInfo.description}
                      {isAdminOnlyCategory && ' (Contact an admin to configure these settings)'}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 ml-14">
                  {availableChannels.map((channel) => {
                    const isDisabled = !localPreferences.masterEnabled || isAdminOnlyCategory ||
                      (channel === 'push' && (isPushLoading || !pushNotifications.isSupported || pushNotifications.permission === 'denied'));
                    return (
                      <div key={channel} className={`flex items-center justify-between p-3 rounded-lg ${isAdminOnlyCategory ? 'bg-gray-100 dark:bg-gray-800/50' : 'bg-card-header'}`}>
                        <div className="flex items-center space-x-2">
                          <ChannelIcon channel={channel} />
                          <span className={`text-sm font-medium ${isAdminOnlyCategory ? 'text-text-disabled' : 'text-text-primary'}`}>{formatChannelName(channel)}</span>
                        </div>
                        <button
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary/50 ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'} ${categorySettings[channel] && localPreferences.masterEnabled && !isAdminOnlyCategory ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'}`}
                          onClick={() => handleCategoryToggle(categoryKey, channel)}
                          disabled={isDisabled} role="switch"
                          aria-checked={categorySettings[channel] && localPreferences.masterEnabled && !isAdminOnlyCategory}
                          title={isAdminOnlyCategory ? 'Admin access required' : undefined}>
                          <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${categorySettings[channel] && localPreferences.masterEnabled && !isAdminOnlyCategory ? 'translate-x-5' : 'translate-x-1'}`} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// --- Sub-component: Push Notification Devices ---
function PushDevicesSection({
  pushNotifications, isPushLoading, handleTestPush, handlePushSubscription,
  pushSubscriptions, unsubscribeFromPush, refetchSubscriptions,
}: {
  pushNotifications: ReturnType<typeof usePushNotifications>;
  isPushLoading: boolean;
  handleTestPush: () => Promise<void>;
  handlePushSubscription: (enable: boolean) => Promise<boolean>;
  pushSubscriptions: { id: string; deviceName?: string | null; browser?: string | null; operatingSystem?: string | null; createdAt?: string | null; lastUsedAt?: string | null; endpoint: string }[] | undefined;
  unsubscribeFromPush: ReturnType<typeof useUnsubscribeFromPushMutation>[0];
  refetchSubscriptions: () => Promise<unknown>;
}) {
  return (
    <div className="bg-card-bg rounded-lg shadow-sm border border-border overflow-hidden mb-8">
      <div className="px-6 py-5 border-b border-border bg-card-header">
        <h2 className="text-lg font-semibold text-text-primary flex items-center">
          <Smartphone className="mr-2" size={20} />Push Notification Devices
        </h2>
        <p className="text-sm text-text-secondary mt-1">Manage push notifications on your devices</p>
      </div>
      <div className="px-6 py-6">
        <div className="p-4 bg-card-header rounded-lg mb-4">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h4 className="text-sm font-medium text-text-primary">Current Browser</h4>
              <p className="text-xs text-text-secondary mt-1">
                {pushNotifications.isSupported
                  ? pushNotifications.isSubscribed ? 'Push notifications enabled on this device' : 'Push notifications not enabled on this device'
                  : 'Push notifications not supported in this browser'}
              </p>
            </div>
            {pushNotifications.isSupported && pushNotifications.permission !== 'denied' && (
              <div className="flex items-center space-x-2">
                {pushNotifications.isSubscribed && (
                  <button onClick={handleTestPush} className="px-3 py-1 text-xs bg-primary/10 text-primary rounded-md hover:bg-primary/20 transition-colors">
                    Send Test
                  </button>
                )}
                <button
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary/50 ${pushNotifications.isSubscribed ? 'bg-primary' : 'bg-gray-300'}`}
                  onClick={() => handlePushSubscription(!pushNotifications.isSubscribed)} disabled={isPushLoading}>
                  <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${pushNotifications.isSubscribed ? 'translate-x-5' : 'translate-x-1'}`} />
                </button>
              </div>
            )}
          </div>
          {pushNotifications.permission === 'denied' && (
            <div className="mt-2 p-3 bg-error/10 text-error rounded text-xs space-y-2">
              <p className="font-medium">Notifications are blocked for this site.</p>
              <p className="text-text-secondary">Your browser remembers that notifications were previously denied. To re-enable:</p>
              <ol className="text-text-secondary list-decimal list-inside space-y-1">
                <li>Click the <strong>lock/tune icon</strong> in your browser&apos;s address bar</li>
                <li>Find <strong>Notifications</strong> and change it to <strong>Allow</strong></li>
                <li>Refresh this page</li>
              </ol>
              <button
                onClick={() => {
                  void Notification.requestPermission().then((perm) => {
                    if (perm === 'granted') window.location.reload();
                    else if (perm === 'denied') showToast.error('Still blocked. Please update your browser site settings and refresh.');
                  });
                }}
                className="mt-1 px-3 py-1.5 text-xs font-medium bg-primary/10 text-primary rounded-md hover:bg-primary/20 transition-colors">
                Re-check Permission
              </button>
            </div>
          )}
        </div>
        {pushSubscriptions && pushSubscriptions.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-text-primary mb-2">Registered Devices</h4>
            {pushSubscriptions.map((sub) => (
              <div key={sub.id} className="p-3 bg-card-header rounded-lg flex items-center justify-between">
                <div>
                  <p className="text-sm text-text-primary">{sub.deviceName || 'Unknown Device'}</p>
                  <p className="text-xs text-text-secondary">
                    {sub.browser} • Last used: {sub.lastUsedAt ? new Date(sub.lastUsedAt).toLocaleDateString() : 'Unknown'}
                  </p>
                </div>
                <button
                  onClick={async () => {
                    try {
                      await unsubscribeFromPush({ endpoint: sub.endpoint }).unwrap();
                      await refetchSubscriptions();
                      showToast.success('Device removed');
                    } catch { showToast.error('Failed to remove device'); }
                  }}
                  className="text-xs text-error hover:text-error/80 transition-colors">
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
